import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useVaccinationStatusCriteria_query$data,
  type useVaccinationStatusCriteria_query$key
} from '@/__generated__/useVaccinationStatusCriteria_query.graphql'
import { type useVaccinationStatusCriteriaMutation } from '@/__generated__/useVaccinationStatusCriteriaMutation.graphql'

type CommitAsyncFn = (vaccinationStatusIds: string[]) => Promise<useVaccinationStatusCriteria_query$data>

export const useVaccinationStatusCriteria = (
  queryRef?: useVaccinationStatusCriteria_query$key | null | undefined
): [string[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useVaccinationStatusCriteria_query on Query {
        currentUser {
          vaccinationStatusCriteria(orderBy: [VACCINATION_STATUS_BY_VACCINATION_STATUS_ID__SEQUENCE_ASC]) {
            nodes {
              vaccinationStatusId
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () => map(queryData?.currentUser?.vaccinationStatusCriteria.nodes, 'vaccinationStatusId'),
    [queryData?.currentUser?.vaccinationStatusCriteria.nodes]
  )
  const [commit, isPending] = useMutation<useVaccinationStatusCriteriaMutation>(graphql`
    mutation useVaccinationStatusCriteriaMutation($input: UpdateVaccinationStatusCriteriaInput!) {
      updateVaccinationStatusCriteria(input: $input) {
        query {
          ...useVaccinationStatusCriteria_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (vaccinationStatusIds: string[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              vaccinationStatusIds
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
